import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Pages, EAGER: the screens somebody actually lands on.
//
// Eager vs lazy below is a deliberate split, not an inconsistency (GitHub
// issue #153). Every page used to be a top-level import, so one bundle held
// the entire application: a user sitting on the login screen downloaded all
// fifteen admin screens, the AI panels and the whole of Tiptap/ProseMirror
// (which arrives via RichContentEditor <- DocumentForm <- the create/edit
// pages) before they could type a password. Splitting these particular ones
// would only trade one round trip for two on the critical path.
import StartPage from './pages/StartPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentViewPage from './pages/DocumentViewPage';
import MyTasksPage from './pages/MyTasksPage';
import ApprovalsPage from './pages/ApprovalsPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';

// Components. All eager -- this is chrome, present on every authenticated
// screen, so there is nothing to defer.
import Sidebar from './components/Sidebar';
import AdminLayout from './components/AdminLayout';
import RequireAuth from './components/RequireAuth';
import RequirePermission from './components/RequirePermission';
import ImpersonationBanner from './components/ImpersonationBanner';
import DelegationBanner from './components/DelegationBanner';
import IdleTimeout from './components/IdleTimeout';
import { AdminIndexRedirect } from './components/adminTabs';
import { AuthProvider, useAuth } from './auth/AuthContext';

// Pages, LAZY: everything reached by a deliberate navigation. The three
// document AUTHORING routes are in here specifically because they are what
// pulls in Tiptap, which is the single largest thing in the bundle and is of
// no use to a reader. Declared below the imports because `import/first`
// requires every static import to come first, and these are calls.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DocumentCreatePage = lazy(() => import('./pages/DocumentCreatePage'));
const DocumentEditPage = lazy(() => import('./pages/DocumentEditPage'));
const RevisionEditPage = lazy(() => import('./pages/RevisionEditPage'));
const RevisionViewPage = lazy(() => import('./pages/RevisionViewPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const ConsolidationPage = lazy(() => import('./pages/ConsolidationPage'));
const HierarchyPage = lazy(() => import('./pages/HierarchyPage'));
const WorkflowsPage = lazy(() => import('./pages/WorkflowsPage'));
const WorkflowDetailPage = lazy(() => import('./pages/WorkflowDetailPage'));
const RegulatoryReferencesPage = lazy(() => import('./pages/admin/RegulatoryReferencesPage'));
const RegulatoryCategoriesPage = lazy(() => import('./pages/admin/RegulatoryCategoriesPage'));
const ChangeCategoriesPage = lazy(() => import('./pages/admin/ChangeCategoriesPage'));
const ReviewFrequenciesPage = lazy(() => import('./pages/admin/ReviewFrequenciesPage'));
const PolicyAreasPage = lazy(() => import('./pages/admin/PolicyAreasPage'));
const ApplicabilityGroupsPage = lazy(() => import('./pages/admin/ApplicabilityGroupsPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/admin/DocumentTemplatesPage'));
const DocumentTypesPage = lazy(() => import('./pages/admin/DocumentTypesPage'));
const DocumentTypeRubricsPage = lazy(() => import('./pages/admin/DocumentTypeRubricsPage'));
const RegulatorySourcesPage = lazy(() => import('./pages/admin/RegulatorySourcesPage'));
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'));
const AcknowledgementsPage = lazy(() => import('./pages/admin/AcknowledgementsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));

/**
 * Everything behind a login: sidebar chrome plus the application routes.
 *
 * Split out from the router so that /login can render WITHOUT the sidebar --
 * before this, every route rendered inside the chrome, so a login screen would
 * have shown a nav menu to someone who is not logged in.
 */
// Issue #176: whether the left nav is hidden. Persisted so the choice survives
// a reload -- IdleTimeout.js's convention, namespaced key plus try/catch around
// every access, because private browsing and quota must degrade rather than
// throw. State lives HERE rather than inside Sidebar because .main-content's
// own left offset depends on it too.
const NAV_HIDDEN_KEY = 'policies:sidebarHidden';

function readNavHidden() {
  try {
    return window.localStorage.getItem(NAV_HIDDEN_KEY) === 'true';
  } catch (error) {
    return false;  // Private browsing / quota -- show the nav, the safe default.
  }
}

function AuthenticatedApp() {
  const { can, user, impersonating, delegation } = useAuth();
  const [navHidden, setNavHidden] = useState(readNavHidden);

  // Issue #221: force a remount of the nav badge and every page on an
  // impersonate/delegate start-or-stop, rather than track down each
  // mount-only fetch individually (the sidebar's My Tasks badge, Start
  // Page's "Recently Viewed", ProfilePage's delegation list,
  // AcknowledgementBar's "is required" answer -- all fetch once on mount
  // with no dependency on WHO is asking, because until now who was asking
  // never changed without a full page load). Changing React's `key` on a
  // subtree is what forces React to tear it down and remount fresh, which is
  // the one thing guaranteed to re-run every mount-only effect underneath it.
  //
  // Two SEPARATE keys, not one on the whole app-container: navHidden lives in
  // THIS component and must survive the switch; ImpersonationBanner and
  // DelegationBanner read live from context and have nothing to refetch; and
  // remounting IdleTimeout would needlessly reset the idle warning clock.
  // Sidebar is keyed on its own because that is what clears the stale badge;
  // Routes is keyed too because it is what re-runs every page's own
  // mount-only fetch. delegation?.id is in the key for the identical bug on
  // the delegate side of issue #44 -- entering/leaving "acting for X" mode.
  const identityKey = `${user?.id ?? 'anon'}:${impersonating}:${delegation?.id ?? ''}`;

  const toggleNav = () => {
    setNavHidden((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(NAV_HIDDEN_KEY, String(next));
      } catch (error) {
        // Degrades to per-session, which is still correct for this tab.
      }
      return next;
    });
  };

  return (
    <div className={`app-container${navHidden ? ' app-container--nav-hidden' : ''}`}>
      <Sidebar key={identityKey} onHide={toggleNav} />
      {/* Rendered only while hidden -- the control that puts the nav back,
          since hiding it takes the in-sidebar toggle off screen with it. */}
      {navHidden && (
        <button
          type="button"
          className="nav-reveal"
          onClick={toggleNav}
          aria-label="Show navigation"
          aria-expanded="false"
          aria-controls="app-sidebar"
        >
          ☰
        </button>
      )}
      <div className="main-content">
        <ImpersonationBanner />
        <DelegationBanner />
        {/* Inside AuthenticatedApp rather than beside the top-level <Routes> so
            it can never render on /login. It is position:fixed, so sitting in
            .main-content does not constrain the overlay. */}
        <IdleTimeout />
        {/* One boundary around the whole route table rather than one per lazy
            route: a route transition swaps exactly one element, so a single
            fallback is all that can ever be showing. `.loading` is the app's
            own treatment (see RequireAuth, which renders the same thing while
            the first /me resolves) -- deliberately not a bare spinner, so a
            chunk fetch reads as the same "working on it" the rest of the app
            uses rather than as a broken screen. The sidebar and banners are
            OUTSIDE it and stay painted throughout. */}
        <Suspense fallback={<div className="loading">Loading…</div>}>
        <Routes key={identityKey}>
          {/* The Start Page (issue #173). Every authenticated user lands here
              and it needs no permission -- it searches the ACTIVE policies the
              caller may already read, and shows nothing else.

              This is what let components/HomeRoute.js be DELETED. That
              component existed because "/" rendered the Dashboard while issue
              #138 gated the Dashboard link on dashboard.read, so a viewer
              without the grant landed on a 403 with no way back and the "*"
              catch-all sent them straight there again. With a universally
              renderable "/" there is no such viewer, and the catch-all now
              always lands somewhere real. */}
          <Route path="/" element={<StartPage />} />
          {/* Moved off "/" by issue #173, and gated here for the first time --
              the link was already gated on dashboard.read, so a pasted or
              bookmarked URL was the remaining way in. */}
          <Route path="/dashboard" element={
            <RequirePermission resource="dashboard"><Dashboard /></RequirePermission>
          } />
          {/* GATED since issue #173. `document_browse` decides whether the
              BROWSE LIST is offered at all, so an organization can take it away
              from a read-only consumer who now finds policies from the Start
              Page. Note every OTHER document route below stays ungated: this
              removes browsing, never reading, or a search result would 403. */}
          <Route path="/documents" element={
            <RequirePermission resource="document_browse"><DocumentsPage /></RequirePermission>
          } />
          {/* /new before /:id -- React Router v6 ranks static segments over
              dynamic ones regardless of order, but declaring it first matches
              this codebase's general caution about path-shadowing (see the
              FastAPI /roles/resources vs /roles/{id} note in CLAUDE.md) and
              costs nothing. */}
          <Route path="/documents/new" element={<DocumentCreatePage />} />
          <Route path="/documents/:id" element={<DocumentViewPage />} />
          <Route path="/documents/:id/edit" element={<DocumentEditPage />} />
          {/* A revision edits a different row under different rules, so it gets
              its own route rather than a mode of the edit page above. */}
          <Route path="/documents/:id/revisions/:revisionId/edit"
                 element={<RevisionEditPage />} />
          {/* Read-only, and deliberately a different route from the /edit one
              above rather than a mode of it -- an approver following this link
              must never land on a form that lets them type into someone else's
              proposal. */}
          <Route path="/documents/:id/revisions/:revisionId"
                 element={<RevisionViewPage />} />
          {/* No RequirePermission -- owning a document requires no permission
              (owner is any authenticated user via /api/users/selectable), same
              reasoning /approvals below is ungated. */}
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          {/* GATED, unlike /my-tasks and /approvals just above. Those two are
              somebody's own work and need no grant; every row on Insights names
              OTHER PEOPLE'S policies and asserts two of them may contradict
              each other, which is why issue #125 gave it a resource of its own
              rather than reusing analytics.read or documents.read. */}
          <Route path="/insights" element={
            <RequirePermission resource="insights"><InsightsPage /></RequirePermission>
          } />
          {/* Issue #111. A sibling path rather than a nested route -- it cannot
              shadow /insights and does not want that page's chrome -- gated on
              the same resource, since this screen names other people's policies
              and sends them to a model. */}
          <Route path="/insights/consolidate" element={
            <RequirePermission resource="insights"><ConsolidationPage /></RequirePermission>
          } />
          {/* Self-service only -- require_identity alone, no RequirePermission
              wrapper, same as its two backend routes. */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/admin" element={<AdminLayout />}>
            {/* Lands on the first tab the viewer can actually see, not a
                hardcoded "hierarchy" -- see adminTabs.js. */}
            <Route index element={<AdminIndexRedirect can={can} />} />
            <Route path="hierarchy" element={
              <RequirePermission resource="hierarchy"><HierarchyPage /></RequirePermission>
            } />
            <Route path="workflows" element={
              <RequirePermission resource="workflows"><WorkflowsPage /></RequirePermission>
            } />
            <Route path="workflows/:id" element={
              <RequirePermission resource="workflows"><WorkflowDetailPage /></RequirePermission>
            } />
            <Route path="regulatory-references" element={
              <RequirePermission resource="regulatory_references"><RegulatoryReferencesPage /></RequirePermission>
            } />
            <Route path="regulatory-categories" element={
              <RequirePermission resource="regulatory_categories"><RegulatoryCategoriesPage /></RequirePermission>
            } />
            <Route path="change-categories" element={
              <RequirePermission resource="change_categories"><ChangeCategoriesPage /></RequirePermission>
            } />
            <Route path="review-frequencies" element={
              <RequirePermission resource="review_frequencies"><ReviewFrequenciesPage /></RequirePermission>
            } />
            <Route path="policy-areas" element={
              <RequirePermission resource="policy_areas"><PolicyAreasPage /></RequirePermission>
            } />
            <Route path="applicability-groups" element={
              <RequirePermission resource="applicability_groups"><ApplicabilityGroupsPage /></RequirePermission>
            } />
            <Route path="document-templates" element={
              <RequirePermission resource="document_templates"><DocumentTemplatesPage /></RequirePermission>
            } />
            <Route path="document-types" element={
              <RequirePermission resource="document_types"><DocumentTypesPage /></RequirePermission>
            } />
            {/* No Resource of its own -- rubric endpoints are guarded on
                document_types, same mapping as the tab (adminTabs.js). */}
            <Route path="document-type-rubrics" element={
              <RequirePermission resource="document_types"><DocumentTypeRubricsPage /></RequirePermission>
            } />
            <Route path="regulatory-sources" element={
              <RequirePermission resource="regulatory_references"><RegulatorySourcesPage /></RequirePermission>
            } />
            <Route path="analytics" element={
              <RequirePermission resource="analytics"><AnalyticsPage /></RequirePermission>
            } />
            <Route path="acknowledgements" element={
              <RequirePermission resource="acknowledgements"><AcknowledgementsPage /></RequirePermission>
            } />
            <Route path="users" element={
              <RequirePermission resource="users"><UsersPage /></RequirePermission>
            } />
            <Route path="settings" element={
              <RequirePermission resource="settings"><SettingsPage /></RequirePermission>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <AuthenticatedApp />
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
